/**
 * Coups de pouce, Attendus 1234 « placer sur une droite graduée un point
 * dont l'abscisse est un nombre relatif » et 1235 « repérer un nombre
 * relatif sur une droite graduée (lire l'abscisse d'un point) » (3e,
 * Repérage). Seconde passe 3e (2026-09-13). UNE page STATIQUE par attendu,
 * le MÊME dessin (la droite de −3 à 0 en dixièmes, le point −2,7), le
 * geste dans un sens puis dans l'autre.
 *
 * L'image que l'élève a déjà : la droite graduée du cycle 3 et de 5e
 * (fiche 1050 « je compte les parts » : les sauts comptés en arcs), et les
 * relatifs de 5e (vers la gauche, les nombres diminuent).
 *   1234 « De −2, je compte 7 sauts vers la gauche » : −2,7 est entre −3
 *     et −2 ; je pars de −2 et je compte 7 dixièmes vers la gauche.
 *   1235 « Entre −3 et −2, puis je compte » : le point est entre −3 et
 *     −2, donc « −2, quelque chose » ; de −2, 7 sauts vers la gauche :
 *     −2,7 (pas −2,3 : je compte depuis −2, pas depuis −3).
 *
 * SACRIFIÉ : −2,7 (droiteRelative.SACRIFIE_D), exclu de tout tirage des
 * deux gens (gen1234-1235.self.test.js).
 */
import { svg } from './1054';

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#64748B';

const ligne = (y, contenu, taille = 12, couleur = ENCRE) => `<text x="200" y="${y}" font-size="${taille}" text-anchor="middle" fill="${couleur}">${contenu}</text>`;

// La droite de −3 à −1, 150 unités par entier (15 par dixième), origine du dessin en x = 40.
const X0 = 40;
const U = 150;
const Y = 70;
const X = (v) => X0 + (v + 3) * U;

const DROITE = [
    `<line x1="${X0 - 16}" y1="${Y}" x2="${X(-1) + 30}" y2="${Y}" stroke="${ENCRE}" stroke-width="1.6"/>`,
    `<polygon points="${X(-1) + 30},${Y} ${X(-1) + 22},${Y - 4} ${X(-1) + 22},${Y + 4}" fill="${ENCRE}"/>`,
    ...Array.from({ length: 21 }, (_, k) => {
        const v = -3 + k / 10;
        const gros = k % 10 === 0;
        return `<line x1="${X(v).toFixed(1)}" y1="${Y - (gros ? 9 : 5)}" x2="${X(v).toFixed(1)}" y2="${Y + (gros ? 9 : 5)}" stroke="${ENCRE}" stroke-width="${gros ? 1.6 : 1}"/>`;
    }),
    ...[-3, -2, -1].map((v) => `<text x="${X(v)}" y="${Y + 26}" font-size="13" font-weight="bold" text-anchor="middle" fill="${ENCRE}">−${-v}</text>`),
].join('\n  ');

/** Les sept sauts de −2 vers −2,7, comptés au-dessus de la droite. */
const SAUTS = Array.from({ length: 7 }, (_, k) => {
    const x1 = X(-2 - k / 10); const x2 = X(-2 - (k + 1) / 10);
    return `<path d="M ${x1} ${Y - 10} Q ${(x1 + x2) / 2} ${Y - 26} ${x2} ${Y - 10}" fill="none" stroke="${ORANGE}" stroke-width="1.6"/>
  <text x="${(x1 + x2) / 2}" y="${Y - 30}" font-size="10" text-anchor="middle" fill="${ORANGE}">${k + 1}</text>`;
}).join('\n  ');

const POINT = `<circle cx="${X(-2.7)}" cy="${Y}" r="4" fill="${ORANGE}"/>
  <text x="${X(-2.7)}" y="${Y + 44}" font-size="14" font-weight="bold" text-anchor="middle" fill="${ORANGE}">−2,7</text>`;
const ENTRE = `<rect x="${X(-3)}" y="${Y - 3}" width="${U}" height="6" fill="${BLEU}" opacity="0.18"/>`;

const DESSIN = `${ENTRE}
  ${DROITE}
  ${SAUTS}
  ${POINT}`;

const SVG_PLACER = svg(
    'Une droite graduée de moins 3 à moins 1, en dixièmes. Moins 2,7 est entre moins 3 et moins 2 : la zone est surlignée en bleu. De moins 2, sept sauts vers la gauche, comptés 1 à 7 en orange, arrivent sur le point moins 2,7. Vers la gauche, les nombres diminuent.',
    `${DESSIN}
  ${ligne(150, `−2,7 est entre <tspan fill="${BLEU}" font-weight="bold">−3 et −2</tspan> : plus petit que −2.`, 13)}
  ${ligne(174, `De −2, je compte <tspan fill="${ORANGE}" font-weight="bold">7 sauts vers la gauche</tspan> (7 dixièmes).`, 13)}
  ${ligne(198, 'Vers la gauche, les nombres diminuent.', 12, GRIS)}`,
    214,
);

const SVG_LIRE = svg(
    'Une droite graduée de moins 3 à moins 1, en dixièmes, un point orange entre moins 3 et moins 2. Le point est entre moins 3 et moins 2 : c’est moins 2 virgule quelque chose. De moins 2, je compte les sauts vers la gauche jusqu’au point : 7. L’abscisse est moins 2,7. Je compte depuis moins 2, pas depuis moins 3.',
    `${DESSIN}
  ${ligne(150, `Le point est entre <tspan fill="${BLEU}" font-weight="bold">−3 et −2</tspan> : c’est « −2, quelque chose ».`, 13)}
  ${ligne(174, `De −2, je compte les sauts vers la gauche : <tspan fill="${ORANGE}" font-weight="bold">7</tspan>, donc −2,7.`, 13)}
  ${ligne(198, 'Je compte depuis −2, pas depuis −3 (ce n’est pas −2,3).', 12, GRIS)}`,
    214,
);

const AIDE_PLACER = { pages: [{ titre: 'De −2, je compte 7 sauts vers la gauche', svg: SVG_PLACER }] };
const AIDE_LIRE = { pages: [{ titre: 'Entre −3 et −2, puis je compte', svg: SVG_LIRE }] };

const CP_1234 = {
    placer_entiers_placer_points: AIDE_PLACER,
    placer_demis_placer_points: AIDE_PLACER,
    placer_dixiemes_placer_points: AIDE_PLACER,
    ou_qcm_unique: AIDE_PLACER,
    combien_saisie: AIDE_PLACER,
    sens_tableau_vf: AIDE_PLACER,
};
export const CP_1235 = {
    abscisse_saisie: AIDE_LIRE,
    abscisse_qcm_unique: AIDE_LIRE,
    nombre_saisie: AIDE_LIRE,
    encadrer_qcm_unique: AIDE_LIRE,
    encadrer_tableau_vf: AIDE_LIRE,
};

export default CP_1234;
